import type { SubmitEvent } from 'react'
import { useMemo } from 'react'
import { useForm } from 'react-hook-form'
import {
	getGetAccountQueryKey,
	useUpdateAccount,
} from '@cocoonspace/sdk-js/domains/account/account'
import { Button } from '@cocoonspace/ui/components/button'
import { Form } from '@cocoonspace/ui/components/form'
import { FormControlledInput } from '@cocoonspace/ui/components/form-controlled-input'
import { Input } from '@cocoonspace/ui/components/input'
import { zodResolver } from '@hookform/resolvers/zod'
import { useLingui } from '@lingui/react/macro'
import { useQueryClient } from '@tanstack/react-query'
import { usePathname } from 'next/navigation'
import { toast } from 'sonner'
import * as z from 'zod'

import { CountrySelect } from '~/components/ui/country-select'
import { stripLocaleFromPath } from '~/i18n/routing'
import { useHttpErrorMsg } from '~/lib/get-http-error-msg'
import { useTracking } from '~/providers/tracking-provider'
import { useUserContext } from '~/providers/user-provider'

export type AddressFactuFormValues = z.infer<typeof schema>

const schema = z.object({
	address: z.string().min(1),
	city: z.string().min(1),
	company: z.string().optional(),
	country_alpha2: z.string().min(1),
	email: z.string().optional(),
	postal_code: z.string().min(1),
	vat_number: z.string().optional(),
})

export const AddressFactuForm = ({
	onSuccess,
}: {
	onSuccess?: VoidFunction
}) => {
	const pathname = stripLocaleFromPath(usePathname() ?? '')
	const queryClient = useQueryClient()
	const { t } = useLingui()
	const { track } = useTracking()
	const httpErrorMsg = useHttpErrorMsg()
	const { accountQuery } = useUserContext()
	const updateAccount = useUpdateAccount()

	const refinedSchema = useMemo(() => {
		if (accountQuery.data?.business) {
			return schema.merge(
				z.object({
					email: z.string().min(1).email(),
				}),
			)
		}

		return schema
	}, [accountQuery.data?.business])

	const form = useForm<AddressFactuFormValues>({
		resolver: zodResolver(refinedSchema),
		values: {
			address: accountQuery.data?.billing_address?.address || '',
			city: accountQuery.data?.billing_address?.city || '',
			company: accountQuery.data?.billing_address?.company || '',
			country_alpha2:
				accountQuery.data?.billing_address?.country_alpha2 || 'FR',
			email: accountQuery.data?.billing_address?.email || '',
			postal_code: accountQuery.data?.billing_address?.postal_code || '',
			vat_number: accountQuery.data?.billing_address?.vat_number || '',
		},
	})

	const onSubmit = (formValues: AddressFactuFormValues) => {
		const query = updateAccount
			.mutateAsync({ data: { billing_address: formValues } })
			.then(() => {
				track.billingAddressUpdate(pathname)

				queryClient.invalidateQueries({ queryKey: getGetAccountQueryKey() })
				onSuccess?.()
			})

		toast.promise(query, {
			loading: t`Modifications en cours...`,
			success: t`Les modifications ont bien été prises en compte.`,
			error: (err: any) => {
				if (err.response?.data?.errors[0]) {
					const { attribute, code } = err.response?.data?.errors[0] || {}

					form.setError(attribute, {
						message: httpErrorMsg[`api.${code}`]
							? httpErrorMsg[`api.${code}`]()
							: t`Une erreur est survenue`,
					})
				}

				return t`Une erreur est survenue`
			},
		})
	}

	const handleFormSubmit = (event: SubmitEvent<HTMLFormElement>) => {
		event.stopPropagation()
		form.handleSubmit(onSubmit)(event)
	}

	return (
		<form
			id='billing-informations-form'
			noValidate
			onSubmit={handleFormSubmit}
		>
			<Form {...form}>
				<div className='grid grid-cols-2 gap-4'>
					<FormControlledInput
						control={form.control}
						label={t`Raison sociale`}
						name='company'
						render={({ field }) => <Input {...field} />}
					/>

					<FormControlledInput
						control={form.control}
						label={t`Numéro de TVA`}
						name='vat_number'
						render={({ field }) => <Input {...field} />}
					/>
				</div>

				<FormControlledInput
					control={form.control}
					label={t`Email de réception des factures`}
					name='email'
					render={({ field }) => (
						<Input
							{...field}
							type='email'
						/>
					)}
				/>

				<div className='grid grid-cols-3 gap-4'>
					<div className='col-span-2'>
						<FormControlledInput
							control={form.control}
							label={t`Adresse`}
							name='address'
							render={({ field }) => <Input {...field} />}
							required
						/>
					</div>

					<FormControlledInput
						control={form.control}
						label={t`Code postal`}
						name='postal_code'
						render={({ field }) => <Input {...field} />}
						required
					/>
				</div>

				<div className='grid grid-cols-2 gap-4'>
					<FormControlledInput
						control={form.control}
						label={t`Ville`}
						name='city'
						render={({ field }) => <Input {...field} />}
						required
					/>

					<FormControlledInput
						control={form.control}
						label={t`Pays`}
						name='country_alpha2'
						render={({ field }) => (
							<CountrySelect
								{...field}
								onValueChange={(value) => field.onChange(value)}
							/>
						)}
						required
					/>
				</div>

				<Button
					className='mt-4 w-full'
					disabled={updateAccount.isPending}
					type='submit'
				>
					{t`Valider`}
				</Button>
			</Form>
		</form>
	)
}
